import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { includes, isEmpty, get, without, min, max } from 'lodash';
import parse from 'html-react-parser';
import sanitize from 'sanitize-html';
import { subscriptionsSVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { useTranslation } from '@com/core/common/translations/i18n';
import { getDays, getWeeklyFrequency } from '@com/core/common/utils/subscriptions';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import BlockButton from '../../common/BlockButton';
import Loader from '../../common/Loader';

type TProps = {
    offerCode: string;
    sku: string;
    subscribeData: any;
    onSubscriptionSuccess: (isSucess: boolean) => void;
};

const SubscribeContent = ({ offerCode, onSubscriptionSuccess, sku, subscribeData }: TProps): JSX.Element | null => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);

    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [isActionLoading, setIsActionLoading] = useState(false);

    const [frequencyType, setFrequencyType] = useState('');
    const [subscriptionDays, setSubscriptionDays] = useState([]);
    const [subscriptionMonthlyDays, setSubscriptionMonthlyDays] = useState<number[]>([]);
    const [subscriptionFrequency, setSubscriptionFrequency] = useState(0);
    const [subscriptionQty, setSubscriptionQty] = useState(1);

    useEffect(() => {
        if (subscribeData) {
            const subType = subscribeData.subType || 'weekly';
            setFrequencyType(subType);

            if (subType === 'weekly') {
                setSubscriptionDays(get(subscribeData.subData, 'dy', []));
            } else {
                setSubscriptionMonthlyDays(get(subscribeData.subData, 'dt', []));
            }
            setSubscriptionFrequency(subType === 'weekly' ? get(subscribeData.subData, 'nt') || 1 : 4);
            setSubscriptionQty(subscribeData.quantity || 1);
        }
    }, [subscribeData]);

    const updateSelectedDays = (day: number) => {
        if (frequencyType === 'weekly') {
            const shouldRemove = includes(subscriptionDays, day);
            setSubscriptionDays(shouldRemove ? without(subscriptionDays, day) : [...subscriptionDays, day]);
        } else {
            const shouldRemoveMonth = includes(subscriptionMonthlyDays, day);
            setSubscriptionMonthlyDays(
                shouldRemoveMonth ? without(subscriptionMonthlyDays, day) : [...subscriptionMonthlyDays, day],
            );
        }
    };

    const subscriptionIsWeekly = frequencyType === 'weekly';

    const submitSubscription = async () => {
        setIsActionLoading(true);
        const daysData = subscriptionIsWeekly ? { dy: subscriptionDays } : { dt: subscriptionMonthlyDays };
        const data = {
            offerCode,
            quantity: subscriptionQty,
            subData: {
                ...daysData,
                nt: subscriptionIsWeekly ? subscriptionFrequency : 1,
            },
            subType: frequencyType,
        };

        try {
            await subscriptionsSVC(locale.apiLocale).createSubscription(sku, data);
            setIsActionLoading(false);
            setActionSuccess(t('subscription_added'));
            onSubscriptionSuccess(true);
        } catch (err) {
            setActionError(err || 'Unable not subscribe to this item. Please try again later.');
            setIsActionLoading(false);
        }
    };
    const minValue = min(subscribeData?.subDiscount.stages);
    const maxValue = subscribeData?.subDiscount.stages.length > 1 ? max(subscribeData.subDiscount.stages) : null;
    const discountString = maxValue ? `${minValue}-${maxValue}%` : `${minValue}%`;

    if (!subscribeData) return null;

    return (
        <SubscribeSection isWeekly={subscriptionIsWeekly}>
            {actionError && <p>{actionError}</p>}
            {/* TODO - FULL SUBSCRIPTION OVERVIEW? */}
            {actionSuccess && <p>{actionSuccess}</p>}
            {!actionError && !actionSuccess && (
                <>
                    <div className="subscribeEnticer">
                        <div>
                            <p className="title">
                                {t('you_can_subscribe_to_this_item_and_save')} {discountString}!
                            </p>
                            <p>{t('enjoy_free_delivery_description')}</p>
                        </div>
                        <img src={`${CDN_IMG_BASE}/creativeIcons/subscriptions-head.svg`} alt="Subscriptions-Head" />
                    </div>

                    <div className="optionGroup">
                        <div className="subscriptionDuration">
                            {getWeeklyFrequency().map(freq => {
                                const isSelected = subscriptionFrequency === freq.id;
                                return (
                                    <div key={freq.id}>
                                        <button
                                            type="button"
                                            className={isSelected ? 'active' : ''}
                                            onClick={() => {
                                                const type = freq.id === 4 ? 'monthly' : 'weekly';
                                                setSubscriptionFrequency(freq.id);
                                                setFrequencyType(type);
                                            }}>
                                            {freq.name}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="optionGroup">
                        <p className="label">{t('select_delivery_days_for_this_item')}</p>
                        <div className="subscriptionOptions">
                            {getDays(subscriptionIsWeekly).map(freq => {
                                const isSelected = includes(
                                    subscriptionIsWeekly ? subscriptionDays : subscriptionMonthlyDays,
                                    freq.id,
                                );
                                return (
                                    <DaysContainer subscriptionIsWeekly={subscriptionIsWeekly} key={freq.id}>
                                        <button
                                            type="button"
                                            className={isSelected ? 'active' : ''}
                                            onClick={() => updateSelectedDays(freq.id)}>
                                            {!subscriptionIsWeekly &&
                                                (isSelected ? (
                                                    <img
                                                        src={`${CDN_IMG_BASE}/simpleIcons/day-white-${
                                                            freq.name.split(' ')[0]
                                                        }.svg`}
                                                        alt="date"
                                                    />
                                                ) : (
                                                    <img
                                                        src={`${CDN_IMG_BASE}/simpleIcons/day-${
                                                            freq.name.split(' ')[0]
                                                        }.svg`}
                                                        alt="date"
                                                    />
                                                ))}
                                            <span>{freq.name}</span>
                                        </button>
                                    </DaysContainer>
                                );
                            })}
                        </div>
                    </div>

                    <div className="optionGroup">
                        <p className="label">{t('how_many_units_of_item_need')}</p>
                        <QuantityStepper>
                            <button
                                type="button"
                                className="removal"
                                onClick={(): void => setSubscriptionQty(subscriptionQty - 1)}
                                disabled={subscriptionQty === subscribeData.quantityMin}>
                                <img src={`${CDN_IMG_BASE}/simpleIcons/minus-brandPrimary.svg`} alt="" />
                            </button>
                            <span className="value">{subscriptionQty}</span>
                            <button
                                type="button"
                                className="addition"
                                onClick={(): void => setSubscriptionQty(subscriptionQty + 1)}
                                disabled={subscriptionQty === subscribeData.quantityMax}>
                                <img src={`${CDN_IMG_BASE}/simpleIcons/plus-brandPrimary.svg`} alt="" />
                            </button>
                        </QuantityStepper>

                        <BlockButton
                            pillStyle
                            onClick={submitSubscription}
                            disabled={
                                isEmpty(subscriptionIsWeekly ? subscriptionDays : subscriptionMonthlyDays) ||
                                !subscriptionFrequency
                            }>
                            {isActionLoading ? <Loader /> : t('subscription-add')}
                        </BlockButton>
                    </div>
                </>
            )}
        </SubscribeSection>
    );
};

const DaysContainer = styled.div<{ subscriptionIsWeekly: boolean }>`
    > button {
        padding: ${({ subscriptionIsWeekly }): string => (subscriptionIsWeekly ? '12px 5px' : '5px 10px')};
    }
`;

const SubscribeSection = styled.div<{ isWeekly: boolean }>`
    padding: 24px 24px 32px;

    .subscribeEnticer {
        background-color: #fffcdc;
        padding: 0 15px 10px;
        font-size: 0.75rem;
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        display: flex;
        margin: 5px 0 15px 0;
        > div {
            padding: 15px 15px 5px 5px;
        }
        > img {
            width: 100px;
            height: 100px;
            margin-top: -10px;
        }
        p.title {
            font-weight: bold;
            margin-bottom: 5px;
            font-size: 0.875rem;
        }
    }

    .frequencySwitch {
        border: 1px solid ${({ theme }): string => theme.colors.border};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        display: flex;
        margin-bottom: 24px;

        button {
            flex: 1;
            padding: 16px 0;
            font-size: 0.875rem;
            color: ${({ theme }): string => theme.colors.bodyLight};
            background-color: ${({ theme }): string => theme.colors.bgTertiary};

            + button {
                ${({ theme: { colors, left } }): string => `border-${left}: 1px solid ${colors.border}`};
            }

            &:hover {
                color: ${({ theme }): string => theme.colors.body};
            }

            &.active {
                color: ${({ theme }): string => theme.colors.body};
                background-color: ${({ theme }): string => theme.colors.bgPrimary};
                box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.1);
                font-weight: 600;
            }
        }
    }

    p.label {
        font-size: 0.9375rem;
        margin-bottom: 20px;
        text-align: center;
        font-weight: 600;
    }

    .optionGroup + .optionGroup {
        margin-top: 30px;
    }
    .subscriptionDuration {
        display: flex;
        margin-bottom: 5px;
        background-color: ${({ theme }): string => theme.colors.bgCancelled};
        border-radius: 53px;
        padding: 4px;
        > div {
            flex: 1;
            ${({ theme: { left } }): string => `padding-${left}: 8px`};
        }
        > div :first-child {
            ${({ theme: { left } }): string => `padding-${left}: 0`};
        }
        button {
            width: 100%;
            padding: 10px 0;
            font-size: 0.75rem;
            color: ${({ theme }): string => theme.colors.bodyMid};
            font-weight: 600;
            &.active {
                box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.12);
                border: none;
                border-radius: 35px;
                background-color: ${({ theme }): string => theme.colors.bodyAlt};
                color: ${({ theme }): string => theme.colors.body};
                font-weight: bold;
            }
        }
        .subscribeActivity {
            display: flex;
            justify-content: space-evenly;
            align-items: center;
            padding: 10px 5px;
            > span {
                text-align: initial;
            }
            > img {
                ${({ theme: { right } }): string => `margin-${right}: 5px`};
            }
        }
    }

    .subscriptionOptions {
        display: flex;

        img {
            width: 24px;
        }

        > div {
            flex: 1;
            ${({ theme: { left } }): string => `padding-${left}: 13px`};
            &:first-child {
                padding: 0;
            }
        }

        button {
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid ${({ theme }): string => theme.colors.border};
            border-radius: ${({ isWeekly }): string => (isWeekly ? '100%' : '25px')};
            min-width: 53px;
            min-height: 53px;
            font-size: 0.875rem;
            width: ${({ isWeekly }): string => (!isWeekly ? '100%' : 'auto')};
            color: ${({ theme }): string => theme.colors.bodyLight};

            > img {
                ${({ theme: { right } }): string => `margin-${right}: 10px`};
                margin-bottom: 5px;
            }

            &:hover {
                border-color: ${({ theme }): string => theme.colors.bodyMid};
            }

            &.active {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
                color: ${({ theme }): string => theme.colors.brandPrimary};
                color: ${({ theme }): string => theme.colors.bodyAlt};
                background-color: ${({ theme }): string => theme.colors.brandPrimary};
                font-weight: 600;
                img {
                    min-width: 22px;
                }
            }
        }
    }
`;

const QuantityStepper = styled.div`
    display: flex;
    align-items: center;
    background-color: rgba(242, 244, 251, 0.8);
    border-radius: 48px;
    padding: 1px;
    margin-bottom: 25px;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;

    .removal {
        background-color: ${({ theme }): string => theme.colors.bgCancelled};
    }
    .addition {
        background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
        border: 1px solid ${({ theme }): string => theme.colors.brandPrimaryLight};
    }

    button {
        width: 48px;
        height: 48px;
        border-radius: 100%;
        border: 1px solid ${({ theme }): string => theme.colors.brandPrimary};
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        display: flex;
        align-items: center;
        justify-content: center;

        &[disabled] {
            cursor: not-allowed;
            opacity: 0.4;
        }

        > img {
            width: 20px;
            height: 20px;
            transition: transform ease-in-out 0.2s;
        }

        &:not([disabled]):hover {
            > img {
                transform: scale(0.8);
            }
        }

        &.addition:not([disabled]):hover {
            > img {
                transform: scale(1.25);
            }
        }
    }

    .value {
        width: 40px;
        text-align: center;
        font-weight: 600;
        font-size: 15px;
    }
`;

export default SubscribeContent;
